import { FRAMES } from "odyssey-shared";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { walletLineWidth } from "../wallet/width.ts";
import type { BagSnapshot } from "./snapshot.ts";
import { say } from "../../words.ts";

/**
 * The Inventory window footer's carry number (P2.8-T53, ADR-125 decision 4.3 and its note for P2.8-T53): bag slots
 * used over the bag's size, its words, and whether the footer is too narrow for it beside the full wallet line, so the
 * wallet's stand-ins draw bare (ADR-124's note for P2.8-T59, item 10) and, where that is not enough, the bar is left out. Widths are worked out from the pixel face's
 * advances, never measured, so the page and its tests agree at any UI scale.
 */

/**
 * The carry number's geometry in logical pixels, the design's Inventory card's `Carry`: the bar 48 by 4 with its edge
 * of 1, the gap of 3 between the bar and the number, and the least gap of 6 between the carry number and the wallet
 * line.
 */
export const CARRY = { bar: 48, barHeight: 4, barEdge: 1, gap: 3, lineGap: 6 } as const;

/**
 * The anchor the carry number's tooltip hangs from, as its `data-tip` attribute.
 */
export const CARRY_TIP_ANCHOR = "carry:0";

/**
 * The bag's slots used and its size.
 */
export interface Carry {
  readonly used: number;
  readonly size: number;
}

/**
 * The slots used and the size a bag snapshot reads: every slot holding an item, a slot past the size included, over
 * the size the last `bag` named.
 *
 * @param bag The snapshot.
 * @returns The carry, or null before the first `bag`.
 * @example
 * carryOf(store.getSnapshot());
 * // { used: 7, size: 20 }
 */
export function carryOf(bag: BagSnapshot): Carry | null {
  if (bag.size === null) {
    return null;
  }
  let used = 0;
  for (const slot of bag.slots) {
    if (slot.item !== null) {
      used += 1;
    }
  }
  return { used, size: bag.size };
}

/**
 * The number the footer draws.
 *
 * @param carry The carry.
 * @returns "used/size".
 * @example
 * carryText({ used: 7, size: 20 });
 * // "7/20"
 */
export function carryText(carry: Carry): string {
  return `${carry.used}/${carry.size}`;
}

/**
 * Whether the bag reads full, which draws the number in the warning colour.
 *
 * @param carry The carry.
 * @returns True when the slots used reach the size.
 */
export function carryFull(carry: Carry): boolean {
  return carry.used >= carry.size;
}

/**
 * The carry number's accessible name, which holds the tooltip's words (ADR-124's note for P2.8-T67, item 3), a draft.
 *
 * @param carry The carry.
 * @returns The name.
 * @example
 * carryName({ used: 7, size: 20 });
 * // "Bag slots used: 7 of 20"
 */
export function carryName(carry: Carry): string {
  return say("bag.carry.name", { used: String(carry.used), size: String(carry.size) });
}

/**
 * The width of the footer's content in a window: the window less its frame's insets and the footer's side padding.
 *
 * @param windowWidth The window's width in logical pixels.
 * @returns The width in logical pixels.
 * @example
 * footerContentWidth(300);
 * // 276
 */
export function footerContentWidth(windowWidth: number): number {
  return windowWidth - FRAMES.window.insets.left - FRAMES.window.insets.right - 2 * KIT_METRICS.window.footer.side;
}

/**
 * How the footer fits the carry number and the wallet line: the stand-ins drawn bare, and the carry number's bar
 * drawn or left out, and the carry number drawn at all.
 */
export interface FooterFit {
  readonly bare: boolean;
  readonly bar: boolean;
  readonly carry: boolean;
}

/**
 * How the footer fits its content, narrowing in stages until it does (ADR-125's note for P2.8-T53, item 5): the
 * carry number and the full wallet line; else the stand-ins drawn without their 0 (ADR-124's note for P2.8-T59,
 * item 10); else the carry number's bar left out too, its number kept; else the carry number left out, which leaves the
 * bare wallet line alone, the least the footer draws. No currency is ever dropped; a window too narrow for the bare
 * wallet line itself (under `walletLineWidth(0, true)` plus the footer's insets) overflows.
 *
 * @param windowWidth The window's width in logical pixels.
 * @param carry The carry number's text, or null where it draws none.
 * @param copper The purse in copper, or null where the footer draws no wallet line.
 * @returns The fit.
 * @throws {RangeError} When the amount is not a whole number in 0 to `CURRENCY_MAX`.
 * @example
 * footerFit(300, "7/20", 12_345_678_999);
 * // { bare: true, bar: false, carry: true }
 */
export function footerFit(windowWidth: number, carry: string | null, copper: number | null): FooterFit {
  if (copper === null) {
    return { bare: false, bar: true, carry: true };
  }
  const room = footerContentWidth(windowWidth);
  const text = carry === null ? 0 : pixelTextWidth(carry) + CARRY.lineGap;
  const bar = carry === null ? 0 : CARRY.bar + CARRY.gap;
  if (bar + text + walletLineWidth(copper, false) <= room) {
    return { bare: false, bar: true, carry: true };
  }
  if (bar + text + walletLineWidth(copper, true) <= room) {
    return { bare: true, bar: true, carry: true };
  }
  if (text + walletLineWidth(copper, true) <= room) {
    return { bare: true, bar: carry === null, carry: true };
  }
  return { bare: true, bar: carry === null, carry: carry === null };
}
